// The order deck: card definitions loaded from data/deck.json, family presentation metadata, and
// legality checking. See spec §5 "the order deck" and plan Task 6.

import deckJson from '../../data/deck.json';
import type { CardDef, CardId, Family, Graphic, Target, TargetKind, Unit } from './types';
import type { Grid } from './grid';

export const CARDS: CardDef[] = deckJson as CardDef[];

const CARD_BY_ID = new Map<CardId, CardDef>(CARDS.map((c) => [c.id, c]));

/** Look up a card definition by id. Throws if the deck data does not define it (a data bug, not a runtime condition to handle gracefully). */
export function card(id: CardId): CardDef {
  const c = CARD_BY_ID.get(id);
  if (!c) throw new Error(`Unknown card id: ${id}`);
  return c;
}

/** Card shape/color presentation per family. Colors reference the CSS custom properties in src/styles.css. */
export interface FamilyMeta { color: string; shape: 'rectangle' | 'diamond' | 'circle' | 'hexagon' | 'chevron' }

export const FAMILY_META: Record<Family, FamilyMeta> = {
  maneuver: { color: 'var(--color-friendly)', shape: 'rectangle' }, // cyan
  fires: { color: 'var(--color-fires)', shape: 'diamond' }, // amber
  isr: { color: 'var(--color-isr)', shape: 'circle' }, // green
  sustain: { color: 'var(--color-sustain)', shape: 'hexagon' }, // violet
  command: { color: 'var(--color-command)', shape: 'chevron' }, // white
};

/** Human-readable phrase describing what each card does, used to build "Only X can <verb>" legality reasons. */
const CARD_VERB: Record<CardId, string> = {
  move: 'move',
  attack: 'attack',
  defend: 'defend',
  screen: 'screen',
  withdraw: 'withdraw',
  fireMission: 'fire missions',
  smoke: 'lay smoke',
  suppress: 'suppress on call',
  launchUas: 'launch a UAS',
  recon: 'recon a route',
  observe: 'observe an NAI',
  resupply: 'resupply',
  casevac: 'run a CASEVAC',
  cancel: 'cancel orders',
  priorityFires: 'set priority of fires',
  mainEffort: 'designate the main effort',
  frago: 'issue a FRAGO',
};

/** "Only X can <verb>" prefix per family. */
const FAMILY_REASON_PREFIX: Record<Family, string> = {
  maneuver: 'Only ground units can',
  fires: 'Only artillery or mortars can',
  isr: 'Only recon or UAS units can',
  sustain: 'Only logistics units can',
  command: 'Only headquarters units can',
};

function targetKindLabel(k: TargetKind): string {
  switch (k) {
    case 'point': return 'a point on the map';
    case 'graphic': return 'a graphic';
    case 'unit': return 'a friendly unit';
    case 'contact': return 'a contact';
    case 'text': return 'order text';
    case 'none': return 'no target';
  }
}

export type Legality = { ok: true } | { ok: false; reason: string };

/**
 * Checks whether issuing `cardId` to `unit` with `target` is legal right now. Pure and synchronous;
 * does not mutate anything. See spec §5 and plan Task 6 for the rules this enforces.
 */
export function isLegal(
  cardId: CardId,
  unit: Unit,
  target: Target,
  grid: Grid,
  graphics: Graphic[],
  units: Unit[],
): Legality {
  const def = CARD_BY_ID.get(cardId);
  if (!def) return { ok: false, reason: `Unknown card: ${cardId}` };

  if (!unit.alive) return { ok: false, reason: 'That unit is destroyed' };

  if (!def.allowedTypes.includes(unit.type)) {
    return { ok: false, reason: `${FAMILY_REASON_PREFIX[def.family]} ${CARD_VERB[cardId]}` };
  }

  if (cardId === 'launchUas' && unit.uasAlive) {
    return { ok: false, reason: 'That airframe is already up' };
  }

  if (target.kind !== def.targetKind) {
    if (def.targetKind === 'none') {
      return { ok: false, reason: cardId === 'cancel' ? 'Cancel needs no target' : `${def.label} needs no target` };
    }
    return { ok: false, reason: `${def.label} needs ${targetKindLabel(def.targetKind)}` };
  }

  switch (def.targetKind) {
    case 'point': {
      const cellCount = grid.w * grid.h;
      if (target.cell === undefined || !Number.isInteger(target.cell) || target.cell < 0 || target.cell >= cellCount) {
        return { ok: false, reason: 'Invalid target cell' };
      }
      // Fires may land anywhere (an impact point is not a maneuver destination); maneuver targets must
      // be passable ground the unit could actually move to.
      if (def.family === 'maneuver' && !grid.passable(target.cell)) {
        return { ok: false, reason: 'Unreachable terrain' };
      }
      break;
    }
    case 'graphic': {
      if (!target.graphicId || !graphics.some((g) => g.id === target.graphicId)) {
        return { ok: false, reason: 'That graphic does not exist' };
      }
      break;
    }
    case 'unit': {
      const targetUnit = target.unitId === undefined ? undefined : units.find((u) => u.id === target.unitId);
      if (!targetUnit) return { ok: false, reason: 'That unit does not exist' };
      if (!targetUnit.alive) return { ok: false, reason: 'That unit is destroyed' };
      break;
    }
    case 'text': {
      if (!target.text || target.text.trim().length === 0) {
        return { ok: false, reason: `${def.label} needs order text` };
      }
      break;
    }
    case 'contact':
    case 'none':
      break;
  }

  return { ok: true };
}
